import React, { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import { api } from "../lib/api";
import { AdminLayout } from "../components/Layout";
import { Card, CardContent, CardHeader, CardTitle } from "../components/ui/card";
import { Button } from "../components/ui/button";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "../components/ui/select";
import { Input } from "../components/ui/input";
import { Timer, Package, Coffee, Download, ArrowLeft, TrendingUp } from "lucide-react";

const fmtMin = (m = 0) => {
  const h = Math.floor(m / 60), mm = m % 60;
  return `${h}h ${mm}m`;
};

export default function AdminProductivity() {
  const navigate = useNavigate();
  const [report, setReport] = useState(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);
  const [startDate, setStartDate] = useState("");
  const [endDate, setEndDate] = useState("");
  const [selectedUser, setSelectedUser] = useState("");

  useEffect(() => {
    // Set default date range (last 7 days)
    const end = new Date();
    const start = new Date();
    start.setDate(start.getDate() - 7);
    setEndDate(end.toISOString().split('T')[0]);
    setStartDate(start.toISOString().split('T')[0]);
  }, []);

  const loadReport = async () => {
    setLoading(true);
    setError(null);
    try {
      const params = new URLSearchParams();
      if (startDate) params.append("start_date", startDate);
      if (endDate) params.append("end_date", endDate);
      if (selectedUser && selectedUser !== "all") params.append("user_id", selectedUser);
      
      const r = await api.get(`/admin/productivity-report?${params.toString()}`);
      setReport(r.data);
    } catch (e) {
      console.error("Failed to load productivity report", e);
      setError(e?.response?.data?.detail || "Failed to load productivity report");
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    if (startDate && endDate) {
      loadReport();
    }
  }, [startDate, endDate, selectedUser]);

  return (
    <AdminLayout>
      <div className="space-y-8">
        <header className="flex items-end justify-between">
          <div>
            <div className="label-overline text-[#D4AF37]">Admin reports</div>
            <h1 className="font-display text-4xl mt-2">Productivity Report</h1>
          </div>
          <Button onClick={() => navigate("/admin")} variant="outline" className="border-[#21362A] text-[#A1B4A8]">
            <ArrowLeft className="w-4 h-4 mr-2" /> Back to Dashboard
          </Button>
        </header>

        <Card className="bg-[#0C140F] border-[#21362A] rounded-sm">
          <CardHeader>
            <CardTitle className="font-display text-xl">Filter Options</CardTitle>
          </CardHeader>
          <CardContent className="grid md:grid-cols-3 gap-4">
            <div>
              <label className="label-overline text-xs mb-2 block">Start Date</label>
              <Input
                type="date"
                value={startDate}
                onChange={(e) => setStartDate(e.target.value)}
                className="bg-[#132018] border-[#21362A] text-white"
              />
            </div>
            <div>
              <label className="label-overline text-xs mb-2 block">End Date</label>
              <Input
                type="date"
                value={endDate}
                onChange={(e) => setEndDate(e.target.value)}
                className="bg-[#132018] border-[#21362A] text-white"
              />
            </div>
            <div>
              <label className="label-overline text-xs mb-2 block">Worker (Optional)</label>
              <Select value={selectedUser} onValueChange={setSelectedUser}>
                <SelectTrigger className="bg-[#132018] border-[#21362A] text-white">
                  <SelectValue placeholder="All workers" />
                </SelectTrigger>
                <SelectContent className="bg-[#132018] border-[#21362A]">
                  <SelectItem value="all">All workers</SelectItem>
                  {report?.report?.map((w) => (
                    <SelectItem key={w.user_id} value={w.user_id}>{w.user_name || w.user_id}</SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>
          </CardContent>
        </Card>

        {loading ? (
          <div className="text-center py-12 text-[#A1B4A8]">Loading productivity data...</div>
        ) : error ? (
          <div className="text-center py-12 text-[#E63946]">{error}</div>
        ) : report ? (
          <>
            <section className="grid grid-cols-2 md:grid-cols-4 gap-6">
              <Kpi icon={Timer} label="Total Workers" value={report.total_workers} />
              <Kpi icon={Package} label="Total Products" value={report.report.reduce((sum, w) => sum + w.total_products_generated, 0)} />
              <Kpi icon={Download} label="Total Exports" value={report.report.reduce((sum, w) => sum + w.total_products_exported, 0)} />
              <Kpi icon={TrendingUp} label="Avg Products/Hour" value={
                report.report.length > 0 
                  ? (report.report.reduce((sum, w) => sum + w.products_per_hour, 0) / report.report.length).toFixed(2)
                  : "0"
              } />
            </section>

            <Card className="bg-[#0C140F] border-[#21362A] rounded-sm">
              <CardHeader>
                <CardTitle className="font-display text-2xl">Worker Performance</CardTitle>
              </CardHeader>
              <CardContent>
                <div className="overflow-x-auto">
                  <table className="w-full text-sm">
                    <thead>
                      <tr className="border-b border-[#21362A]">
                        <th className="text-left py-3 px-4 text-[#A1B4A8] font-medium">Worker</th>
                        <th className="text-right py-3 px-4 text-[#A1B4A8] font-medium">Shifts</th>
                        <th className="text-right py-3 px-4 text-[#A1B4A8] font-medium">Total Time</th>
                        <th className="text-right py-3 px-4 text-[#A1B4A8] font-medium">Work Time</th>
                        <th className="text-right py-3 px-4 text-[#A1B4A8] font-medium">Break Time</th>
                        <th className="text-right py-3 px-4 text-[#A1B4A8] font-medium">Idle Time</th>
                        <th className="text-right py-3 px-4 text-[#A1B4A8] font-medium">Products</th>
                        <th className="text-right py-3 px-4 text-[#A1B4A8] font-medium">Exports</th>
                        <th className="text-right py-3 px-4 text-[#A1B4A8] font-medium">Prod/Hr</th>
                      </tr>
                    </thead>
                    <tbody>
                      {report.report.map((worker) => (
                        <tr key={worker.user_id} className="border-b border-[#21362A]/50 hover:bg-[#132018]/50">
                          <td className="py-3 px-4 font-medium text-white">{worker.user_name || worker.user_id}</td>
                          <td className="text-right py-3 px-4 text-[#A1B4A8]">{worker.shifts}</td>
                          <td className="text-right py-3 px-4 text-[#A1B4A8]">{fmtMin(worker.total_shift_minutes)}</td>
                          <td className="text-right py-3 px-4 text-[#D4AF37]">{fmtMin(worker.total_work_minutes)}</td>
                          <td className="text-right py-3 px-4 text-[#A1B4A8]">{fmtMin(worker.total_break_minutes)}</td>
                          <td className="text-right py-3 px-4 text-[#E63946]">{fmtMin(worker.total_idle_minutes)}</td>
                          <td className="text-right py-3 px-4 text-[#D4AF37] font-semibold">{worker.total_products_generated}</td>
                          <td className="text-right py-3 px-4 text-[#097969]">{worker.total_products_exported}</td>
                          <td className="text-right py-3 px-4 text-white">{worker.products_per_hour}</td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
                {report.report.length === 0 && (
                  <div className="text-center py-8 text-[#A1B4A8]">No productivity data for the selected period</div>
                )}
              </CardContent>
            </Card>
          </>
        ) : null}
      </div>
    </AdminLayout>
  );
}

function Kpi({ icon: Icon, label, value, tone = "default" }) {
  const toneColors = {
    default: "text-[#D4AF37]",
    danger: "text-[#E63946]",
    success: "text-[#097969]",
  };
  return (
    <Card className="bg-[#0C140F] border-[#21362A] rounded-sm">
      <CardContent className="p-5">
        <div className="flex items-center gap-3">
          <Icon className={`w-5 h-5 ${toneColors[tone]}`} />
          <div>
            <div className="label-overline text-xs">{label}</div>
            <div className="font-display text-2xl mt-1">{value}</div>
          </div>
        </div>
      </CardContent>
    </Card>
  );
}
